import classnames from 'classnames'
import * as React from 'react'
import ReactDOM from 'react-dom'

import { formatValue } from './utils'

var getOffset = function (el: Element) {
  var clientRect = el.getBoundingClientRect()
  return {
    top: clientRect.top + window.pageYOffset,
    left: clientRect.left + window.pageXOffset,
  }
}

interface DraggablePos {
  x: number
  y: number
}

interface DraggableProps {
  type: string
  initialPos: DraggablePos
  onChanged: (pos: number) => void
  onChange: (pos: number) => void
  style: React.CSSProperties
  onMouseEnter: () => void
  onMouseLeave: () => void
  tooltipValue: any
  tooltipFormat?: string
  tooltipShown: boolean
}

interface DraggableState {
  pos: DraggablePos
  dragging: boolean
  hovered: boolean
  rel: DraggablePos | null
  touch: boolean
}

class Draggable extends React.Component<DraggableProps, DraggableState> {
  static defaultProps = {
    initialPos: { x: 0, y: 0 },
  }

  state: DraggableState = {
    pos: this.props.initialPos,
    dragging: false,
    hovered: false,
    rel: null,
    touch: false,
  }

  handleRef = React.createRef<HTMLDivElement>()

  componentDidUpdate(props: DraggableProps, state: DraggableState) {
    if (this.state.dragging && !state.dragging) {
      if (this.state.touch) {
        document.addEventListener('touchmove', this.onMouseMove)
        document.addEventListener('touchend', this.onMouseUp)
      } else {
        document.addEventListener('mousemove', this.onMouseMove)
        document.addEventListener('mouseup', this.onMouseUp)
      }
    } else if (!this.state.dragging && state.dragging) {
      if (this.state.touch) {
        document.removeEventListener('touchmove', this.onMouseMove)
        document.removeEventListener('touchend', this.onMouseUp)
      } else {
        document.removeEventListener('mousemove', this.onMouseMove)
        document.removeEventListener('mouseup', this.onMouseUp)
      }
    }
  }

  onMouseDown = (e: MouseEvent) => {
    if (e.button !== 0 || !this.handleRef.current) {
      return
    }
    var offset = getOffset(this.handleRef.current)
    this.setState({
      dragging: true,
      touch: false,
      rel: {
        x: e.pageX - offset.left,
        y: e.pageY - offset.top,
      },
    })
    e.stopPropagation()
    e.preventDefault()
  }

  onTouchStart = (e: TouchEvent) => {
    if (!this.handleRef.current) return

    var offset = getOffset(this.handleRef.current)
    this.setState({
      dragging: true,
      touch: true,
      rel: {
        x: e.touches[0].pageX - offset.left,
        y: e.touches[0].pageY - offset.top,
      },
    })
    e.stopPropagation()
    e.preventDefault()
  }

  onMouseUp = (e: MouseEvent | TouchEvent) => {
    this.setState({ dragging: false })
    this.props.onChanged(this.props.type === 'vertical' ? this.state.pos.y : this.state.pos.x)
    e.stopPropagation()
    e.preventDefault()
  }

  onMouseMove = (e: MouseEvent | TouchEvent) => {
    if (!this.state.dragging) return

    var pageX = this.state.touch ? (e as TouchEvent).touches[0].pageX : (e as MouseEvent).pageX
    var pageY = this.state.touch ? (e as TouchEvent).touches[0].pageY : (e as MouseEvent).pageY
    // @ts-ignore
    var offset = getOffset(ReactDOM.findDOMNode(this).parentNode.parentNode)
    var changedPos = { x: this.state.pos.x, y: this.state.pos.y }
    if (this.props.type === 'vertical') {
      changedPos.y = pageY - offset.top - this.state.rel!.y - (this.props.style.marginTop as any)
    } else {
      changedPos.x = pageX - offset.left - this.state.rel!.x - (this.props.style.marginLeft as any)
    }

    var retVal = this.props.onChange(this.props.type === 'vertical' ? changedPos.y : changedPos.x)
    if (typeof retVal !== 'undefined') {
      if (this.props.type === 'vertical') {
        // @ts-ignore
        this.setState({ pos: { y: retVal } })
      } else {
        // @ts-ignore
        this.setState({ pos: { x: retVal } })
      }
    } else {
      this.setState({ pos: changedPos })
    }

    e.stopPropagation()
    e.preventDefault()
  }

  onMouseEnter = () => {
    this.setState({ hovered: true })
    this.props.onMouseEnter()
  }

  onMouseLeave = () => {
    this.setState({ hovered: false })
    this.props.onMouseLeave()
  }

  render() {
    const isVertical = this.props.type === 'vertical'

    return (
      <div
        ref={this.handleRef}
        onMouseDown={this.onMouseDown as any}
        onTouchStart={this.onTouchStart as any}
        onMouseEnter={this.onMouseEnter}
        onMouseLeave={this.onMouseLeave}
        className={classnames('tooltips absolute z-10 touch-none', {
          'is-vertical': isVertical,
          'is-horizontal': !isVertical,
        })}
        style={{
          ...this.props.style,
          left: `${this.state.pos.x}px`,
          top: `${this.state.pos.y}px`,
        }}
      >
        <div className={this.props.tooltipShown ? 'is-shown' : ''}>
          {formatValue(this.props.tooltipValue, this.props.tooltipFormat)}
        </div>
      </div>
    )
  }

  isDragged = () => this.state.dragging

  isHovered = () => this.state.hovered
}

export default Draggable
